import { useEffect, useState, useMemo } from "react";
import { adminFunnelLaunch } from "@/lib/api";
import { TrendingDown, RefreshCw } from "lucide-react";

const WINDOWS = [
    { days: 7, label: "7 d" },
    { days: 30, label: "30 d" },
    { days: 90, label: "90 d" },
];

// Real-time launch conversion funnel: Sign-ups → Uploads → Reports → Payments.
// Pure DB aggregation on the backend (no PostHog round-trip), so it refreshes
// instantly and is safe to poll. Renders as horizontal bars sized to
// `pct_of_top` with drop-off percentages between stages.
export default function LaunchFunnel({ includeTest = false } = {}) {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async (d = days) => {
        setLoading(true);
        try {
            const r = await adminFunnelLaunch(d, { include_test: includeTest });
            setData(r);
        } catch {
            /* non-blocking */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(days); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, includeTest]);

    const stages = data?.stages || [];
    const topCount = stages[0]?.count || 0;

    // Sparkline for the daily timeseries: stacked signups+uploads+payments per day.
    const sparkPoints = useMemo(() => {
        const daily = data?.daily || [];
        if (!daily.length) return [];
        const maxN = Math.max(1, ...daily.map((d) => Math.max(d.signups, d.uploads, d.payments)));
        return daily.map((d, i) => ({
            x: (i / Math.max(1, daily.length - 1)) * 100,
            signups: (d.signups / maxN) * 100,
            uploads: (d.uploads / maxN) * 100,
            payments: (d.payments / maxN) * 100,
            date: d.date,
        }));
    }, [data]);

    const buildPath = (key) => {
        if (!sparkPoints.length) return "";
        return sparkPoints
            .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${(100 - p[key]).toFixed(2)}`)
            .join(" ");
    };

    return (
        <div className="bl-card p-5 mt-6" data-testid="admin-funnel-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                        Launch conversion funnel
                    </div>
                    <div className="mt-1 text-sm text-bl-muted">
                        Distinct-user counts at each stage of the
                        Sign-up → Upload → Report → Paid journey.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-md border border-bl overflow-hidden">
                        {WINDOWS.map((w) => (
                            <button
                                key={w.days}
                                type="button"
                                onClick={() => setDays(w.days)}
                                className={
                                    "px-3 py-1.5 text-xs font-medium transition-colors "
                                    + (days === w.days
                                        ? "bg-[var(--bl-moss)] text-white"
                                        : "text-bl-muted hover:bg-bl-bg")
                                }
                                data-testid={`admin-funnel-window-${w.days}`}
                            >
                                {w.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => load()}
                        disabled={loading}
                        className="bl-btn-ghost p-1.5"
                        title="Refresh"
                        data-testid="admin-funnel-refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>
            </div>

            <div className="mt-5 space-y-3" data-testid="admin-funnel-stages">
                {stages.map((s, i) => {
                    const pctOfTop = topCount > 0 ? (s.count / topCount) * 100 : 0;
                    const widthPct = Math.max(2, pctOfTop); // minimum 2% so 0 still shows the label
                    return (
                        <div
                            key={s.name}
                            className="relative"
                            data-testid={`admin-funnel-stage-${i}`}
                        >
                            <div className="flex items-center justify-between text-xs mb-1">
                                <div className="font-display font-semibold text-bl flex items-center gap-2">
                                    <span className="inline-flex w-5 h-5 rounded-full bg-bl-bg border border-bl items-center justify-center text-[10px] text-bl-muted">
                                        {i + 1}
                                    </span>
                                    {s.name}
                                </div>
                                <div className="flex items-center gap-3 text-bl-muted">
                                    {s.drop_from_prev_pct !== null && s.drop_from_prev_pct !== undefined && (
                                        <span
                                            className={
                                                "inline-flex items-center gap-0.5 text-[10px] "
                                                + (s.drop_from_prev_pct > 50
                                                    ? "text-[var(--bl-rust)]"
                                                    : s.drop_from_prev_pct > 25
                                                        ? "text-[var(--bl-amber)]"
                                                        : "text-[var(--bl-moss)]")
                                            }
                                            data-testid={`admin-funnel-drop-${i}`}
                                        >
                                            <TrendingDown className="w-3 h-3" />
                                            −{s.drop_from_prev_pct}%
                                        </span>
                                    )}
                                    <span
                                        className="font-display font-semibold text-bl tnum"
                                        data-testid={`admin-funnel-count-${i}`}
                                    >
                                        {s.count.toLocaleString()}
                                    </span>
                                    <span className="text-[10px] tabular-nums w-12 text-right">
                                        {s.pct_of_top}%
                                    </span>
                                </div>
                            </div>
                            <div className="h-7 bg-bl-bg border border-bl rounded-md overflow-hidden">
                                <div
                                    className="h-full transition-all duration-500 bg-gradient-to-r from-[var(--bl-moss)] to-[var(--bl-moss-tint)]"
                                    style={{ width: `${widthPct}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Daily timeseries — 3-series mini sparkline */}
            {sparkPoints.length > 1 && (
                <div className="mt-5" data-testid="admin-funnel-sparkline">
                    <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1">
                        <span>Daily activity · {days}d</span>
                        <div className="flex items-center gap-3 text-[10px] normal-case tracking-normal">
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-3 h-[2px] bg-[var(--bl-moss)]" />
                                Sign-ups
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-3 h-[2px] bg-[var(--bl-amber)]" />
                                Uploads
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-3 h-[2px] bg-[var(--bl-rust)]" />
                                Payments
                            </span>
                        </div>
                    </div>
                    <svg
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        className="w-full h-20 border border-bl rounded-md bg-bl-bg"
                    >
                        <path
                            d={buildPath("signups")}
                            fill="none"
                            stroke="var(--bl-moss)"
                            strokeWidth="0.8"
                            vectorEffect="non-scaling-stroke"
                        />
                        <path
                            d={buildPath("uploads")}
                            fill="none"
                            stroke="var(--bl-amber)"
                            strokeWidth="0.8"
                            vectorEffect="non-scaling-stroke"
                        />
                        <path
                            d={buildPath("payments")}
                            fill="none"
                            stroke="var(--bl-rust)"
                            strokeWidth="0.8"
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>
                </div>
            )}
        </div>
    );
}
